import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { QaHistoryDsService, PatternInfoDsService, ActiveOrderDsService } from '@app/core/data-services';
import { NextSetupSubphaseCommandService, PreviousSetupSubphaseCommandService } from '@app/modules/setup-phase/commands';
import { SetupPhaseService } from '@app/modules/setup-phase/services/setup-phase/setup-phase.service';
import { OrderPhaseService } from '@app/shared/components';
import { nav, setupPhaseFooterRoutes } from '@app/shared/utils';
import { LabelType, ProductionOrderPrintedLabelInfo, Quantity, RunPhaseType, RunSubPhaseType } from 'chronos-core-client';
import { SetupParametersService } from '@app/modules/setup-phase/services/setup-parameters/setup-parameters.service';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ParametersFactoryService } from '@app/core/services/parameters-factory';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-setup-parameters',
  templateUrl: './setup-parameters.component.html',
  styleUrls: ['./setup-parameters.component.scss']
})
export class SetupParametersComponent implements OnInit, OnDestroy {
  public printInfo$: Observable<ProductionOrderPrintedLabelInfo>;
  public backCommandService: PreviousSetupSubphaseCommandService;
  public nextCommandService: NextSetupSubphaseCommandService;
  public form = new UntypedFormGroup({});
  public productionOrderId: number;
  public tgsSampleQuantity: Quantity;
  public aqlSampleQuantity: Quantity;
  public quantityChanged = new EventEmitter<LabelType>();

  private subscriptions = new Subscription();
  private areParametersValid = false;

  constructor(
    private setupParametersService: SetupParametersService,
    private qaHistoryDsService: QaHistoryDsService,
    private setupPhaseService: SetupPhaseService,
    private setupFooterService: OrderPhaseService,
    private parametersFactoryService: ParametersFactoryService,
    private activeOrderDsService: ActiveOrderDsService,
    private patternInfoDsService: PatternInfoDsService
  ) {}

  public ngOnInit(): void {
    this.parametersFactoryService.setValuesForSetup();
    this.printInfo$ = this.setupParametersService.printInfo$;
    this.setupParametersService.getPrintInfoForAllLabels();
    this.productionOrderId = this.activeOrderDsService.getActiveOrderId();
    const activeOrder = this.activeOrderDsService.getActiveOrder();

    this.aqlSampleQuantity = activeOrder.aqlSampleQuantity;
    this.tgsSampleQuantity = activeOrder.tgsSampleQuantity;

    this.setCommands();

    this.subscriptions.add(
      combineLatest([
        this.qaHistoryDsService.isQaHistoryAvailableForSetup(),
        this.qaHistoryDsService.isLineClearanceEnableForSetup(),
        this.patternInfoDsService.isPatternAvailableForProductionOrder(this.productionOrderId)
      ]).subscribe(([isQaHistoryAvailable, isLineClearanceEnable, isPatternAvailable]) => {
        this.updateBackCommand(isQaHistoryAvailable, isLineClearanceEnable, isPatternAvailable);
      })
    );

    this.subscriptions.add(
      this.qaHistoryDsService.isQaChecksAvailableForSetup().subscribe((isAvailable) => {
        this.updateNextCommand(isAvailable);
      })
    );

    this.subscriptions.add(
      this.form.statusChanges.subscribe(() => {
        this.validateNextCommand();
      })
    );
  }

  public ngOnDestroy(): void {
    this.parametersFactoryService.cleanParametersFactoryValues();
  }

  public onQuantityChanged(labelType: LabelType): void {
    this.quantityChanged.emit(labelType);
  };

  private setCommands(): void {
    this.backCommandService = new PreviousSetupSubphaseCommandService(this.setupFooterService);
    this.nextCommandService = new NextSetupSubphaseCommandService(this.setupFooterService);

    this.setupPhaseService.setSetupCommands(this.backCommandService, this.nextCommandService);
  }

  private updateBackCommand(isQaHistoryAvailable: boolean, isLineClearanceAvailable: boolean, isPatternAvailable: boolean): void {
    if (isPatternAvailable) {
      this.backCommandService.setNavigationParams(nav.routes.setupPatternInfo);
      this.backCommandService.enabled = true;
    } else if (isQaHistoryAvailable) {
      this.backCommandService.setNavigationParams(nav.routes.setupQaHistory);
      this.backCommandService.enabled = true;
    } else if (isLineClearanceAvailable) {
      this.backCommandService.setNavigationParams(nav.routes.setupLineClearance);
      this.backCommandService.enabled = true;
      this.setBackCommandExecution();
    } else {
      this.backCommandService.enabled = false;
    }
  }

  private updateNextCommand(isQaAvailable: boolean): void {
    if (isQaAvailable) {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupInProgressFinishingCheck, nav.routes.qualityAssurance);
    } else {
      this.nextCommandService.setNavigationParams(RunSubPhaseType.SetupMaterialWaste, nav.routes.wasteAssignment);
    }
  }

  public onParameterValidation(isValid: boolean) {
    this.areParametersValid = isValid;
    this.validateNextCommand();
  }

  private validateNextCommand(): void {
    this.nextCommandService.enabled = this.form.valid && this.areParametersValid;
  }

  private setBackCommandExecution() {
    this.backCommandService.execute = () => {
      this.setupFooterService
        .goToNextSubPhase(nav.routes.setupLineClearance, RunPhaseType.Setup, RunSubPhaseType.SetupLineClearance, setupPhaseFooterRoutes)
        .pipe(finalize(() => {}))
        .subscribe();
    };
  }
}
